<script setup>
import { computed } from 'vue'
import ShowScheduleVerticalBlock from '@components/show-schedule-vertical-block'
import formatDate from '@utils/format-date-full'
import { useStore } from '@composables/use-store'
import ChannelOptions from './channel-options'

const props = defineProps({
    channel: {
        type: Object,
        default: () => ({
            id: 'channelId',
            title: 'channelTitle',
            servers: {},
            playlist: [],
        }),
    },
    activeSlide: {
        type: Number,
        default: 0,
    },
    index: {
        type: Number,
        default: 0,
    },
    playlist: {
        type: Array,
        default: () => [],
    },
    displayMinimalVersion: {
        type: Boolean,
        default: true,
    },
})

const store = useStore()
const resource = computed(() => store.state.playback.resource)
const status = computed(() => store.state.playback.status)
const nowPlaying = computed(() => props.playlist[0])
const nowPlayingAvailable = computed(() => nowPlaying.value?.mixes)

function airDate(airStart) {
    return airStart ? formatDate(new Date(airStart)) : ''
}
</script>

<template>
    <div>
        <!-- Channel Detail Tile -->
        <BaseLink
            :to="{ name: 'channel', params: { channelId: channel.id } }"
            class="channel-link"
            name="channel"
        >
            <BasePlaceholder
                :text="channel.title"
                :tag="displayMinimalVersion ? 'h6' : 'h5'"
                :class="['channel-title', { minimal: displayMinimalVersion }]"
                type="channel"
            />
        </BaseLink>

        <!-- COVER AREA -->
        <div :class="['cover-area', { active: index === activeSlide }]">
            <div :class="['bg', channel.id]">
                <div
                    v-if="nowPlayingAvailable && nowPlaying.mixes.album_art"
                    class="image-bg"
                    :style="`background-image:url(${nowPlaying.mixes.album_art.url})`"
                />
                <div class="gradient-bg" />
            </div>
            <div :class="['cover-inner', channel.id]">
                <div class="cover-image">
                    <BaseResourceImage
                        v-if="nowPlayingAvailable"
                        :resource="nowPlaying.mixes.episode"
                        :should-lazyload="false"
                        radius="card"
                        class="image"
                    />
                </div>
                <div class="cover-image-blur">
                    <BaseResourceImage
                        v-if="nowPlayingAvailable"
                        :resource="nowPlaying.mixes.episode"
                        :should-lazyload="false"
                        radius="card"
                        class="image"
                    />
                </div>
                <BaseResourceCover
                    v-if="channel"
                    :media-resource="channel"
                    class="overlay"
                    media-type="channel"
                >
                    <div slot="secondaryActions">
                        <ChannelOptions :channel="channel" />
                    </div>
                </BaseResourceCover>

                <!--
        <div
          v-else
          :class="$style.stopped">
          <BaseButton
            :class="$style.playButton"
            @click="playRadioChannel(channel)">
            <span
              :class="$style.buttonTitle"
              title="PLAY">PLAY</span>
          </BaseButton>
        </div> -->
            </div>
        </div>
        <!-- END OF COVER AREA -->

        <!-- NOW PLAYING AREA -->
        <div
            v-if="channel"
            class="now-playing-area"
        >
            <div class="heading now-playing-head">Now Playing</div>
            <div class="now-playing-content">
                <BaseLink
                    v-if="nowPlayingAvailable"
                    :to="{ path: `/artist/${nowPlaying.mixes.artist.url}/${nowPlaying.mixes.artist.id}` }"
                >
                    <div
                        :class="[
                            'title',
                            'now-playing-title',
                            { active: status === 'playing' && resource && resource.id === channel.id },
                        ]"
                    >
                        {{ nowPlaying.mixes.artist.title }}
                    </div>
                </BaseLink>
                <BasePlaceholder
                    v-else
                    :class="[
                        'title',
                        'now-playing-title',
                        { active: status === 'playing' && resource && resource.id === channel.id },
                    ]"
                    :text="nowPlayingAvailable && nowPlaying.mixes.artist.title"
                    type="artist"
                    tag="div"
                />
                <BaseLink
                    v-if="nowPlayingAvailable && nowPlaying.mixes.id"
                    :to="{ name: 'mix', params: { id: nowPlaying.mixes.id } }"
                >
                    <div class="date">{{ airDate(nowPlaying.mixes.episode.air_start) }}</div>
                </BaseLink>
                <BasePlaceholder
                    v-else
                    class="date"
                    :text="nowPlayingAvailable && airDate(nowPlaying.mixes.episode.air_start)"
                    :size="5"
                    tag="div"
                />
                <BaseLink
                    v-if="nowPlayingAvailable && nowPlaying.mixes.show"
                    :to="{ name: 'show', params: { id: nowPlaying.mixes.show.id } }"
                >
                    <div class="subtitle highlight now-playing-artist">
                        {{ nowPlaying.mixes.show.title }}
                    </div>
                </BaseLink>
                <BasePlaceholder
                    v-else
                    class="subtitle highlight now-playing-artist"
                    :text="nowPlayingAvailable && nowPlaying.mixes.show.title"
                    type="show"
                    tag="div"
                />
            </div>
            <div class="mobile-play-button">
                <BaseButtonPlay
                    :resource="{ type: 'channel', id: channel.id }"
                    icon
                />
            </div>
        </div>
        <!-- END OF NOW PLAYING AREA -->

        <!-- UPCOMING SCHEDULE AREA -->
        <ShowScheduleVerticalBlock
            v-if="!displayMinimalVersion"
            :playlist="playlist"
        />
        <!-- END OF UPCOMING SCHEDULE AREA -->
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

// COVER AREA
.channel-title {
    margin: $s-s 0;
    text-align: center;
    &:hover {
        color: $c-highlight;
    }
}

.channel-link {
    &:hover,
    &:active {
        text-decoration: none;
    }
}

.cover-area {
    position: relative;
    height: 230px;
    transform: scale(1.2, 0.7) translateX(0);
    transition: transform 0.3s ease-in-out;
    &.active {
        transform: scale(0.8) translateX(0);
    }

    &:hover {
        @media (min-width: $desktop) {
            .cover-image {
                opacity: 0;
            }
            .cover-image-blur {
                opacity: 1;
            }
        }
    }
}

.gradient-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 230px;
    opacity: 0.4;
}

.image-bg {
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100% 100%;
    filter: blur(30px);
}

.bg {
    @include fill;

    &.frisky {
        .gradient-bg {
            background: $c-frisky-channel-grad;
        }
    }
    &.chill {
        .gradient-bg {
            background: $c-chill-channel-grad;
        }
    }
    &.deep {
        .gradient-bg {
            background: $c-deep-channel-grad;
        }
    }
    &.classics {
        .gradient-bg {
            background: $c-classics-channel-grad;
        }
    }
}

.cover-image {
    @include center;

    opacity: 1;
    transition: opacity 0.3s ease-in-out;
    .image {
        width: 160px;
    }
}

.cover-image-blur {
    @include center;

    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    .image {
        width: 160px;
        filter: blur(10px);
    }
}

.cover-inner {
    height: 100%;
    &.classics .cover-image {
        filter: grayscale(100%);
    }
    &.classics .cover-image-blur {
        filter: grayscale(100%);
    }
}

//  NOW PLAYING AREA
.now-playing-area {
    min-height: 68px;
    margin: 16px 0 40px;
    text-align: center;
}
.now-playing-head {
    font-size: 11px;
    color: $c-text-faded;
}
.now-playing-content {
    display: inline-block;
}
.now-playing-title {
    max-width: 270px;
    margin: 8px 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 16px;
    font-weight: 900;
    line-height: 24px;
    color: $c-white;
    white-space: nowrap;
    &.active {
        color: $c-razzmatazz;
    }
}

.date {
    @extend %contentSubtitleM;

    margin-bottom: $s-xxs;
}

.now-playing-artist {
    @extend %contentSubtitleL;

    margin: $s-xxs 0 0;
    font-size: 13px;
}

.mobile-play-button {
    width: 150px;
    margin: 30px auto;
    .button {
        text-align: left;
    }
    .title {
        margin-left: $size-grid-padding * 2;
        font-size: 12px;
        font-weight: bold;
        vertical-align: middle;
        letter-spacing: 1px;
    }
}

.overlay {
    height: 100%;
}

@media (min-width: $tablet) {
    .cover-area {
        transform: unset;
        &.active {
            transform: unset;
        }
    }
}

@media (min-width: $desktop) {
    .channel-title {
        text-align: left;
        &.minimal {
            text-align: center;
        }
    }
    .now-playing-head {
        letter-spacing: normal;
    }
    .mobile-play-button {
        display: none;
    }
}
</style>
